import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faArrowToLeft } from '@fortawesome/pro-solid-svg-icons';
import _ from 'lodash';
import {
    Grid,
    Button,
    Drawer,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Typography,
    Slider,
    TextField,
    Switch,
    FormControlLabel,
    Checkbox,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
// eslint-disable-next-line import/no-unresolved
import { CheckboxList } from '@commonClient2/components';
// eslint-disable-next-line no-unused-vars
import TotalHoursDisplay from '../TotalHoursDisplay';
import {
    UPDATE_SELECTION_FILTERS,
    updateShowWeeklyHours,
} from '../../../store/bidSelection';
// eslint-disable-next-line no-unused-vars
import './ShiftlineFilterSelectionBar.scss';
import { ScheduleSource, PeriodType, ShiftlineFilterSelectionTabs, LocationAndJobFilterType } from '../../../Enums';

const getPositiveMod = (/** @type {number} */ n, /** @type {number} */ mod) => ((n % mod) + mod) % mod;
/**
 * @param {DayOfWeek} anchorDay
 * @param {PeriodType} periodType
 * @param {number} dayCount
 * @param {Function} tCommon
 * @returns {import("@commonClient2/components/CheckboxList/typing").CheckboxListItem[]} defaultDaySelectionItems
 */
const defaultDaySelectionItems = (anchorDay, periodType, dayCount, tCommon) => {
    const arr = [
        {
            id: getPositiveMod(0 - anchorDay, 7),
            name: periodType === PeriodType.Weeks ? tCommon('dayOfWeek.Sunday') : `Day ${getPositiveMod(0 - anchorDay, 7) + 1}`,
            filterIndex: periodType === PeriodType.Weeks ? 0 : getPositiveMod(0 - anchorDay, 7),
            checked: true,
            total: 0,
        },
        {
            id: getPositiveMod(1 - anchorDay, 7),
            name: periodType === PeriodType.Weeks ? tCommon('dayOfWeek.Monday') : `Day ${getPositiveMod(1 - anchorDay, 7) + 1}`,
            filterIndex: periodType === PeriodType.Weeks ? 1 : getPositiveMod(1 - anchorDay, 7),
            checked: true,
            total: 0,
        },
        {
            id: getPositiveMod(2 - anchorDay, 7),
            name: periodType === PeriodType.Weeks ? tCommon('dayOfWeek.Tuesday') : `Day ${getPositiveMod(2 - anchorDay, 7) + 1}`,
            filterIndex: periodType === PeriodType.Weeks ? 2 : getPositiveMod(2 - anchorDay, 7),
            checked: true,
            total: 0,
        },
        {
            id: getPositiveMod(3 - anchorDay, 7),
            name: periodType === PeriodType.Weeks ? tCommon('dayOfWeek.Wednesday') : `Day ${getPositiveMod(3 - anchorDay, 7) + 1}`,
            filterIndex: periodType === PeriodType.Weeks ? 3 : getPositiveMod(3 - anchorDay, 7),
            checked: true,
            total: 0,
        },
        {
            id: getPositiveMod(4 - anchorDay, 7),
            name: periodType === PeriodType.Weeks ? tCommon('dayOfWeek.Thursday') : `Day ${getPositiveMod(4 - anchorDay, 7) + 1}`,
            filterIndex: periodType === PeriodType.Weeks ? 4 : getPositiveMod(4 - anchorDay, 7),
            checked: true,
            total: 0,
        },
        {
            id: getPositiveMod(5 - anchorDay, 7),
            name: periodType === PeriodType.Weeks ? tCommon('dayOfWeek.Friday') : `Day ${getPositiveMod(5 - anchorDay, 7) + 1}`,
            filterIndex: periodType === PeriodType.Weeks ? 5 : getPositiveMod(5 - anchorDay, 7),
            checked: true,
            total: 0,
        },
        {
            id: getPositiveMod(6 - anchorDay, 7),
            name: periodType === PeriodType.Weeks ? tCommon('dayOfWeek.Saturday') : `Day ${getPositiveMod(6 - anchorDay, 7) + 1}`,
            filterIndex: periodType === PeriodType.Weeks ? 6 : getPositiveMod(6 - anchorDay, 7),
            checked: true,
            total: 0,
        },
    ];

    return arr.filter((x) => x.filterIndex < dayCount);
};
const defaultHoursSelectionItems = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

/**
 * @param {{
 * isSelectedButtonHidden?: boolean
 * showAutoOrderButton?: boolean
 * setShowAutoOrderModal?: Function
 * periodType: PeriodType
 * dayCount: number
 * weeklyHours?: boolean
 * weeklyHoursByDefault?: boolean
 * }} props
 */
function ShiftlineFilterSelectionBar(props) {
    const dispatch = useDispatch();
    /** @type {ShiftBidding.EmployeeStore} */
    // @ts-ignore
    const { employeeBidSelection, publicConfigs } = useSelector((state) => state);
    const {
        items, scheduleSource, selectionFilters, shifts, anchorDay,
    } = employeeBidSelection;
    const { selectedTab } = selectionFilters;
    const isShowAll = selectedTab === ShiftlineFilterSelectionTabs.Unselected;

    // Get the location and job filter type from config
    const locationAndJobFilterType = publicConfigs?.employeeFilters?.locationAndJobFilterType ?? LocationAndJobFilterType.LocationsAndJobs;
    const isCostCenterMode = locationAndJobFilterType === LocationAndJobFilterType.CostCenters;
    const {
        isSelectedButtonHidden = false,
        showAutoOrderButton = false,
        setShowAutoOrderModal = () => { },
        periodType = PeriodType.Weeks,
        dayCount = 7,
        weeklyHours = false,
        weeklyHoursByDefault = false,
    } = props;
    const { t: tShiftlines } = useTranslation('shiftBidding', { keyPrefix: 'employee.shiftlines' });
    const { t: tCommon } = useTranslation('common');
    const [isInitiated, setInitiated] = useState(false);
    const [drawerExtended, setDrawerExtended] = useState(false);
    const [dayOfWeekAccordionExpanded, setDayOfWeekAccordionExpanded] = useState(true);
    const [startDateAccordionExpanded, setStartDateAccordionExpanded] = useState(true);
    const [locationAccordionExpanded, setLocationAccordionExpanded] = useState(true);
    const [jobAccordionExpanded, setJobAccordionExpanded] = useState(true);
    const [workplanAccordionExpanded, setWorkplanAccordionExpanded] = useState(true);
    const [nbAppliedFilters, setNbAppliedFilters] = useState(0);

    // Day selection
    /** @type {[import("@commonClient2/components/CheckboxList/typing").CheckboxListItem[], Function]} */
    const [daySelectionItems, setDaySelectionItems] = useState(
        _.cloneDeep(defaultDaySelectionItems(anchorDay, periodType, dayCount, tCommon).sort((a, b) => a.id - b.id)),
    );

    // Start date selection
    const [startDateHours, setStartDateHours] = useState([0, 24]);
    const [shiftsPerHours, setShiftsPerHours] = useState(_.cloneDeep(defaultHoursSelectionItems));

    const [isLocationIncludeTransfer, setLocationIncludeTransfer] = useState(true);
    const [isJobIncludeTransfer, setJobIncludeTransfer] = useState(true);

    // Primary Location selection
    /** @type {[import("@commonClient2/components/CheckboxList/typing").CheckboxListItem[], Function]} */
    const [primaryLocationItems, setPrimaryLocationItems] = useState([]);

    // Primary & JTS Location selection
    /** @type {[import("@commonClient2/components/CheckboxList/typing").CheckboxListItem[], Function]} */
    const [transferLocationItems, setTransferLocationItems] = useState([]);

    // Primary Job selection
    /** @type {[import("@commonClient2/components/CheckboxList/typing").CheckboxListItem[], Function]} */
    const [primaryJobItems, setPrimaryJobItems] = useState([]);

    // Primary & JTS Job selection
    /** @type {[import("@commonClient2/components/CheckboxList/typing").CheckboxListItem[], Function]} */
    const [transferJobItems, setTransferJobItems] = useState([]);

    // Workplan selection
    /** @type {[import("@commonClient2/components/CheckboxList/typing").CheckboxListItem[], Function]} */
    const [workplanItems, setWorkplanItems] = useState([]);

    const onToggleLocationItem = (/** @type {import("@commonClient2/components/CheckboxList/typing").CheckboxListItem} */ item) => {
        const primary = primaryLocationItems.find((x) => x.id === item.id);
        if (primary) {
            primary.checked = item.checked;
            setPrimaryLocationItems([...primaryLocationItems]);
        }
        const transfer = transferLocationItems.find((x) => x.id === item.id);
        if (transfer) {
            transfer.checked = item.checked;
            setTransferLocationItems([...transferLocationItems]);
        }
    };

    const onToggleJobItem = (/** @type {import("@commonClient2/components/CheckboxList/typing").CheckboxListItem} */ item) => {
        const primary = primaryJobItems.find((x) => x.id === item.id);
        if (primary) {
            primary.checked = item.checked;
            setPrimaryJobItems([...primaryJobItems]);
        }
        const transfer = transferJobItems.find((x) => x.id === item.id);
        if (transfer) {
            transfer.checked = item.checked;
            setTransferJobItems([...transferJobItems]);
        }
    };

    const isAllLocationChecked = (isLocationIncludeTransfer ? transferLocationItems : primaryLocationItems).every((x) => x.checked);
    const isAllLocationIndeterminate = !isAllLocationChecked && (isLocationIncludeTransfer ? transferLocationItems : primaryLocationItems).some((x) => x.checked);

    const toggleAllLocations = () => {
        const primary = primaryLocationItems.map((x) => ({ ...x, checked: !isAllLocationChecked }));
        const transfer = transferLocationItems.map((x) => ({ ...x, checked: !isAllLocationChecked }));
        setPrimaryLocationItems([...primary]);
        setTransferLocationItems([...transfer]);
    };

    const isAllJobChecked = (isJobIncludeTransfer ? transferJobItems : primaryJobItems).every((x) => x.checked);
    const isAllJobIndeterminate = !isAllJobChecked && (isJobIncludeTransfer ? transferJobItems : primaryJobItems).some((x) => x.checked);
    const toggleAllJobs = () => {
        const primary = primaryJobItems.map((x) => ({ ...x, checked: !isAllJobChecked }));
        const transfer = transferJobItems.map((x) => ({ ...x, checked: !isAllJobChecked }));
        setPrimaryJobItems([...primary]);
        setTransferJobItems([...transfer]);
    };

    /**
     * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems[]} itemList
     * @param {BidProfile.ISchedulePatternsShiftTemplateDictionary} updatedShifts
     */
    const calculateCounters = (itemList, updatedShifts) => {
        /** @type {import("./typing").counterList} */
        const shiftsPerHrs = {};
        /** @type {import("./typing").counterList} */
        const shiftsPerDays = {};
        /** @type {import("./typing").counterList} */
        const primaryLocations = {};
        /** @type {import("./typing").counterList} */
        const primaryJobs = {};
        /** @type {import("./typing").counterList} */
        const primaryAndTransferLocations = {};
        /** @type {import("./typing").counterList} */
        const primaryAndTransferJobs = {};
        /** @type {import("./typing").counterList} */
        const workplans = {};
        let totalShifts = 0;

        for (const item of itemList) {
            // In cost center mode, use the full org path as location and don't track jobs
            const primaryLocationKey = isCostCenterMode
                ? item.fullOrgJobPath
                : item.primaryLocation;
            const primaryJobKey = isCostCenterMode
                ? null
                : item.primaryJob;

            if (primaryLocations[primaryLocationKey]) {
                primaryLocations[primaryLocationKey]++;
            } else {
                primaryLocations[primaryLocationKey] = 1;
            }

            if (primaryJobKey && (primaryJobs[primaryJobKey])) {
                primaryJobs[primaryJobKey]++;
            } else if (primaryJobKey) {
                primaryJobs[primaryJobKey] = 1;
            }

            if (primaryAndTransferLocations[primaryLocationKey]) {
                primaryAndTransferLocations[primaryLocationKey]++;
            } else {
                primaryAndTransferLocations[primaryLocationKey] = 1;
            }

            for (const transferLocation of item.transferLocations) {
                if (primaryAndTransferLocations[transferLocation]) {
                    primaryAndTransferLocations[transferLocation]++;
                } else {
                    primaryAndTransferLocations[transferLocation] = 1;
                }
            }

            if (primaryJobKey && (primaryAndTransferJobs[primaryJobKey])) {
                primaryAndTransferJobs[primaryJobKey]++;
            } else if (primaryJobKey) {
                primaryAndTransferJobs[primaryJobKey] = 1;
            }

            // In cost center mode, don't process transfer jobs
            if (!isCostCenterMode) {
                for (const transferJob of item.transferJobs) {
                    if (primaryAndTransferJobs[transferJob]) {
                        primaryAndTransferJobs[transferJob]++;
                    } else {
                        primaryAndTransferJobs[transferJob] = 1;
                    }
                }
            }

            if (item.workplanName) {
                if (workplans[item.workplanName]) {
                    workplans[item.workplanName]++;
                } else {
                    workplans[item.workplanName] = 1;
                }
            }

            const patternItems = item.schedulePatternTemplate.items;
            totalShifts += patternItems.length;
            const dates = [false, false, false, false, false, false, false];
            for (const patternItem of patternItems) {
                // TODO Seb - 2021-09-23 / Set login for dynamic day of the week (for now 0 = sunday)
                const day = (patternItem.day - 1) % 7;
                if (!dates[day]) {
                    dates[day] = true;
                }
                const shiftTemplate = updatedShifts[patternItem.shiftTemplate];
                const actualHour = Math.floor(shiftTemplate.startTime / 60 / 60);
                if (shiftsPerHrs[actualHour]) {
                    shiftsPerHrs[actualHour]++;
                } else {
                    shiftsPerHrs[actualHour] = 1;
                }
            }

            for (let i = 0; i < dates.length; i++) {
                if (dates[i]) {
                    if (shiftsPerDays[i]) {
                        shiftsPerDays[i]++;
                    } else {
                        shiftsPerDays[i] = 1;
                    }
                }
            }
        }

        return {
            shiftsPerHrs,
            shiftsPerDays,
            primaryLocations,
            primaryJobs,
            primaryAndTransferLocations,
            primaryAndTransferJobs,
            workplans,
            totalShifts,
        };
    };

    const handleConfirm = () => {
        const acceptedDayItems = daySelectionItems.filter((x) => x.checked).map((x) => x.id);
        const acceptedLocationItems = isLocationIncludeTransfer
            ? transferLocationItems.filter((x) => x.checked).map((x) => x.name)
            : primaryLocationItems.filter((x) => x.checked).map((x) => x.name);

        // In cost center mode, don't include job items
        const jobItemsToFilter = isJobIncludeTransfer
            ? transferJobItems.filter((x) => x.checked).map((x) => x.name)
            : primaryJobItems.filter((x) => x.checked).map((x) => x.name);
        const acceptedJobItems = isCostCenterMode ? [] : jobItemsToFilter;

        const acceptedWorkplanItems = workplanItems.filter((x) => x.checked).map((x) => x.name);
        const actualStartDateHours = startDateHours.map((x) => x * 60 * 60);

        dispatch({
            type: UPDATE_SELECTION_FILTERS,
            payload: {
                daySelectionItems: acceptedDayItems,
                startDateHours: actualStartDateHours,
                isLocationIncludeTransfer,
                locationItems: acceptedLocationItems,
                isJobIncludeTransfer: isCostCenterMode ? false : isJobIncludeTransfer,
                jobItems: acceptedJobItems,
                workplanItems: acceptedWorkplanItems,
                isCostCenterMode,
            },
        });

        setDrawerExtended(false);

        const daySelectionApplied = daySelectionItems.filter((x) => !x.checked).length;
        const locationItemsApplied = transferLocationItems.filter((x) => !x.checked).length;
        const jobItemsApplied = isCostCenterMode ? 0 : transferJobItems.filter((x) => !x.checked).length;
        const workplanItemsApplied = workplanItems.filter((x) => !x.checked).length;
        const isHoursChanged = (!startDateHours.includes(0) || !startDateHours.includes(24)) ? 1 : 0;

        setNbAppliedFilters(daySelectionApplied + locationItemsApplied + jobItemsApplied + workplanItemsApplied + isHoursChanged);
    };

    const handleReset = () => {
        const daySelection = daySelectionItems.map((x) => ({ ...x, checked: true }));
        const workplan = workplanItems.map((x) => ({ ...x, checked: true }));
        const primaryLocations = primaryLocationItems.map((x) => ({ ...x, checked: true }));
        const transferLocations = transferLocationItems.map((x) => ({ ...x, checked: true }));
        const primaryJobs = primaryJobItems.map((x) => ({ ...x, checked: true }));
        const transferJobs = transferJobItems.map((x) => ({ ...x, checked: true }));

        setLocationIncludeTransfer(true);
        setJobIncludeTransfer(!isCostCenterMode);
        setDaySelectionItems([...daySelection]);
        setPrimaryLocationItems([...primaryLocations]);
        setTransferLocationItems([...transferLocations]);
        setPrimaryJobItems([...primaryJobs]);
        setTransferJobItems([...transferJobs]);
        setWorkplanItems([...workplan]);
        setStartDateHours([0, 24]);
        setNbAppliedFilters(0);
    };

    /**
     * @param {EmployeeBidProfile.IGroupedScheduleTemplateItems[]} itemList
     * @param {BidProfile.ISchedulePatternsShiftTemplateDictionary} updatedShifts
     */
    const handleItemsChanged = (itemList, updatedShifts) => {
        if (itemList && !isInitiated) {
            const {
                shiftsPerHrs,
                shiftsPerDays,
                primaryLocations,
                primaryJobs,
                primaryAndTransferLocations,
                primaryAndTransferJobs,
                workplans,
                totalShifts,
            } = calculateCounters(itemList, updatedShifts);

            const newDaySelectionItems = _.cloneDeep(defaultDaySelectionItems(anchorDay, periodType, dayCount, tCommon).sort((a, b) => a.id - b.id));
            for (const [key, value] of Object.entries(shiftsPerDays)) {
                newDaySelectionItems[parseInt(key, 10)].total = value;
            }
            setDaySelectionItems(newDaySelectionItems);

            const newHourItems = _.cloneDeep(defaultHoursSelectionItems);
            for (const [key, value] of Object.entries(shiftsPerHrs)) {
                newHourItems[parseInt(key, 10)] = Math.round((value / totalShifts) * 100);
            }
            setShiftsPerHours(newHourItems);

            const newPrimaryLocItems = [];
            for (const [key, value] of Object.entries(primaryLocations)) {
                newPrimaryLocItems.push({
                    id: key,
                    name: key,
                    checked: true,
                    total: value,
                });
            }
            setPrimaryLocationItems(newPrimaryLocItems);

            const newTransferLocItems = [];
            for (const [key, value] of Object.entries(primaryAndTransferLocations)) {
                newTransferLocItems.push({
                    id: key,
                    name: key,
                    checked: true,
                    total: value,
                });
            }
            setTransferLocationItems(newTransferLocItems);

            const newPrimaryJobItems = [];
            for (const [key, value] of Object.entries(primaryJobs)) {
                newPrimaryJobItems.push({
                    id: key,
                    name: key,
                    checked: true,
                    total: value,
                });
            }
            setPrimaryJobItems(newPrimaryJobItems);

            const newTransferJobItems = [];
            for (const [key, value] of Object.entries(primaryAndTransferJobs)) {
                newTransferJobItems.push({
                    id: key,
                    name: key,
                    checked: true,
                    total: value,
                });
            }
            setTransferJobItems(newTransferJobItems);

            const newWorkplanItems = [];
            for (const [key, value] of Object.entries(workplans)) {
                if (value) {
                    newWorkplanItems.push({
                        id: key,
                        name: key,
                        checked: true,
                        total: value,
                    });
                }
            }
            setWorkplanItems(newWorkplanItems);

            setInitiated(true);
        }
    };

    useEffect(() => {
        handleItemsChanged(items, shifts);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(items)]);

    /**
     * @param {string} tab
     */
    const handleTabSelected = (tab) => {
        const acceptedDayItems = daySelectionItems.filter((x) => x.checked).map((x) => x.id);
        const acceptedLocationItems = isLocationIncludeTransfer
            ? transferLocationItems.filter((x) => x.checked).map((x) => x.name)
            : primaryLocationItems.filter((x) => x.checked).map((x) => x.name);
        const acceptedJobItems = isJobIncludeTransfer
            ? transferJobItems.filter((x) => x.checked).map((x) => x.name)
            : primaryJobItems.filter((x) => x.checked).map((x) => x.name);
        const acceptedWorkplanItems = workplanItems.filter((x) => x.checked).map((x) => x.name);
        const actualStartDateHours = startDateHours.map((x) => x * 60 * 60);

        dispatch({
            type: UPDATE_SELECTION_FILTERS,
            payload: {
                selectedTab: tab,
                daySelectionItems: acceptedDayItems,
                startDateHours: actualStartDateHours,
                isLocationIncludeTransfer,
                locationItems: acceptedLocationItems,
                isJobIncludeTransfer,
                jobItems: acceptedJobItems,
                workplanItems: acceptedWorkplanItems,
                isCostCenterMode,
            },
        });
    };

    /**
     * @param {number} index
     * @param {string} value
     */
    const handleTimeInputChange = (index, value) => {
        if (value) {
            const values = value.split(':');
            if (values.length === 2 && values[0] !== '' && values[1] !== '') {
                const hrs = parseInt(values[0], 10);
                const mins = Math.floor(parseInt(values[1], 10) / 15) * 0.25;
                const closestTime = hrs + mins;
                if (closestTime >= 0 && closestTime <= 24) {
                    const startDate = [...startDateHours];
                    startDate[index] = closestTime;
                    if (startDate[0] <= startDate[1]) {
                        setStartDateHours(startDate);
                    }
                }
            }
        }
    };

    const drawStartTimeSelection = () => {
        /**
         * @param {number} index
         */
        const generateHour = (index) => {
            const selectedClassName = index >= startDateHours[0] && index <= startDateHours[1] ? 'VisualiserSelected' : '';
            return (
                <div
                    key={`StartTimeSelectionVisualiserColumn_${index}`}
                    className={`StartTimeSelectionVisualiserColumn ${selectedClassName}`}
                    style={{ height: `${shiftsPerHours[index] * 2}px` }}
                />
            );
        };

        const drawVisualiser = () => {
            const visualiser = [];
            for (let i = 0; i < 24; i++) {
                visualiser.push(generateHour(i));
            }
            return visualiser;
        };

        /**
         * @param {number} value
         */
        const formatValueTo12H = (value) => {
            /**
             * @param {number} num
             * @param {number} places
             */
            const zeroPad = (num, places) => String(num).padStart(places, '0');

            const minutes = ((value % 1) * 60);
            const hr = value > 12 ? Math.floor(value - 12) : Math.floor(value);

            return `${zeroPad(hr, 2)}:${zeroPad(minutes, 2)}`;
        };

        /**
         * @param {number} value
         */
        const formatValueTo24H = (value) => {
            /**
             * @param {number} num
             * @param {number} places
            */
            const zeroPad = (num, places) => String(num).padStart(places, '0');
            const minutes = ((value % 1) * 60);
            const hr = Math.floor(value % 24);

            return `${zeroPad(hr, 2)}:${zeroPad(minutes, 2)}`;
        };

        return (
            <div className="DrawerStartTimeSelection">
                <div className="DrawerStartTimeSelectionVisualiser">
                    {
                        drawVisualiser()
                    }
                </div>
                <div className="DrawerStartTimeSelectionSlider">
                    <Slider
                        value={startDateHours}
                        min={0}
                        step={0.25}
                        max={24}
                        valueLabelFormat={(value) => formatValueTo12H(value)}
                        valueLabelDisplay="auto"
                        marks={[
                            {
                                value: 0,
                                label: '0AM',
                            },
                            {
                                value: 6,
                                label: '6AM',
                            },
                            {
                                value: 12,
                                label: '12PM',
                            },
                            {
                                value: 18,
                                label: '6PM',
                            },
                            {
                                value: 24,
                                label: '12AM',
                            },
                        ]}
                        // @ts-ignore
                        onChange={(_e, val) => setStartDateHours(val)}
                    />
                    <div className="DrawerStartTimeSelectionInputContainer">
                        <div className="DrawerStartTimeSelectionInput">
                            <TextField
                                label={tShiftlines('Filters.StartTimeFrom')}
                                variant="outlined"
                                value={formatValueTo24H(startDateHours[0])}
                                type="time"
                                onChange={(e) => handleTimeInputChange(0, e.target.value)}
                            />
                        </div>
                        <div className="DrawerStartTimeSelectionInput">
                            <TextField
                                label={tShiftlines('Filters.StartTimeTo')}
                                variant="outlined"
                                value={formatValueTo24H(startDateHours[1])}
                                type="time"
                                onChange={(e) => handleTimeInputChange(1, e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="filterSelectionBarContainer">
            <Drawer
                className="filterSelectionBarDrawer"
                variant="persistent"
                anchor="left"
                classes={{ paper: 'filterSelectionBarDrawerPaper' }}
                open={drawerExtended}
                onClose={() => setDrawerExtended(false)}
            >
                <div className="filterSelectionBarDrawerHeader">
                    <Grid
                        container
                        justifyContent="space-between"
                    >
                        <Grid
                            item
                            xs={6}
                            className="filterSelectionBarDrawerHeaderTitleText"
                        >
                            <FontAwesomeIcon
                                icon={faFilter}
                            />
                            {tShiftlines('Filters.Filters')}
                        </Grid>

                        <Grid
                            item
                            xs={2}
                            className="FilterButtonAlignRight"
                        >
                            <Button
                                className="filterSelectionBarDrawerCloseButton"
                                onClick={() => { setDrawerExtended(false); }}
                            >
                                <FontAwesomeIcon
                                    icon={faArrowToLeft}
                                />
                            </Button>
                        </Grid>

                    </Grid>
                </div>

                <Accordion
                    className="DrawerAccordion"
                    expanded={dayOfWeekAccordionExpanded}
                    onChange={() => setDayOfWeekAccordionExpanded(!dayOfWeekAccordionExpanded)}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="accordion1-content"
                        id="accordion1-header"
                    >
                        <Typography className="DrawerAccordionTitle">
                            {periodType === PeriodType.Weeks ? tShiftlines('Filters.DayOfTheWeek') : tShiftlines('Filters.DayInPattern')}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <CheckboxList
                            useNumericTotals
                            items={daySelectionItems}
                            onChange={(
                                /** @type {import("@commonClient2/components/CheckboxList/typing").CheckboxListItem[]} */ newItems,
                            ) => { setDaySelectionItems(newItems); }}
                        />
                    </AccordionDetails>
                </Accordion>

                <Accordion
                    className="DrawerAccordion"
                    expanded={startDateAccordionExpanded}
                    onChange={() => setStartDateAccordionExpanded(!startDateAccordionExpanded)}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="accordion2-content"
                        id="accordion2-header"
                    >
                        <Typography className="DrawerAccordionTitle">{tShiftlines('Filters.StartTime')}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {drawStartTimeSelection()}
                    </AccordionDetails>
                </Accordion>

                <Accordion
                    className="DrawerAccordion"
                    expanded={locationAccordionExpanded}
                    onChange={() => setLocationAccordionExpanded(!locationAccordionExpanded)}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="accordion3-content"
                        id="accordion3-header"
                    >
                        <Typography className="DrawerAccordionTitle">
                            {isCostCenterMode ? tShiftlines('Filters.LocationsAndJobs') : tShiftlines('Filters.Locations')}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <div className="fullWidthContainer">
                            <div className="accordionFilterHeaderOptions">
                                <Checkbox
                                    className="checkAllCheckbox"
                                    color="primary"
                                    checked={isAllLocationChecked}
                                    indeterminate={isAllLocationIndeterminate}
                                    onChange={(event) => { toggleAllLocations(); }}
                                />
                                {!isCostCenterMode && (
                                    <FormControlLabel
                                        control={(
                                            <Checkbox
                                                color="primary"
                                                checked={isLocationIncludeTransfer}
                                                onChange={(event) => { setLocationIncludeTransfer(event.target.checked); }}
                                            />
                                        )}
                                        label={tShiftlines('includeTransfers')}
                                        style={{ marginRight: '5px' }}

                                    />
                                )}
                            </div>
                            <hr className="separatorSmall" />
                            {
                                <CheckboxList
                                    items={isLocationIncludeTransfer ? transferLocationItems : primaryLocationItems}
                                    onChange={(
                                        /** @type {import("@commonClient2/components/CheckboxList/typing").CheckboxListItem[]} */
                                        newItems,
                                        /** @type {import("@commonClient2/components/CheckboxList/typing").CheckboxListItem} */
                                        changedItem,
                                    ) => { onToggleLocationItem(changedItem); }}
                                />
                            }
                        </div>
                    </AccordionDetails>
                </Accordion>

                {!isCostCenterMode && (
                    <Accordion
                        className="DrawerAccordion"
                        expanded={jobAccordionExpanded}
                        onChange={() => setJobAccordionExpanded(!jobAccordionExpanded)}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="accordion3-content"
                            id="accordion3-header"
                        >
                            <Typography className="DrawerAccordionTitle">{tShiftlines('Filters.Jobs')}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <div className="fullWidthContainer">
                                <div className="accordionFilterHeaderOptions">
                                    <Checkbox
                                        className="checkAllCheckbox"
                                        color="primary"
                                        checked={isAllJobChecked}
                                        indeterminate={isAllJobIndeterminate}
                                        onChange={(event) => { toggleAllJobs(); }}
                                    />
                                    <FormControlLabel
                                        control={(
                                            <Checkbox
                                                color="primary"
                                                checked={isJobIncludeTransfer}
                                                onChange={(event) => { setJobIncludeTransfer(event.target.checked); }}
                                            />
                                        )}
                                        label={tShiftlines('includeTransfers')}
                                        style={{ marginRight: '5px' }}
                                    />
                                </div>
                                <hr className="separatorSmall" />
                                {
                                    <CheckboxList
                                        useNumericTotals
                                        items={isJobIncludeTransfer ? transferJobItems : primaryJobItems}
                                        onChange={(
                                            /** @type {import("@commonClient2/components/CheckboxList/typing").CheckboxListItem[]} */
                                            newItems,
                                            /** @type {import("@commonClient2/components/CheckboxList/typing").CheckboxListItem} */
                                            changedItem,
                                        ) => { onToggleJobItem(changedItem); }}
                                    />
                                }
                            </div>
                        </AccordionDetails>
                    </Accordion>
                )}
                {
                    scheduleSource === ScheduleSource.WorkplanManager
                        ? (
                            <Accordion
                                className="DrawerAccordion"
                                expanded={workplanAccordionExpanded}
                                onChange={() => setWorkplanAccordionExpanded(!workplanAccordionExpanded)}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls="accordion4-content"
                                    id="accordion4-header"
                                >
                                    <Typography className="DrawerAccordionTitle">{tShiftlines('Filters.WorkplanName')}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <CheckboxList
                                        useNumericTotals
                                        items={workplanItems}
                                        onChange={(
                                            /** @type {import("@commonClient2/components/CheckboxList/typing").CheckboxListItem[]} */
                                            newItems,
                                        ) => { setWorkplanItems(newItems); }}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        )
                        : null
                }
            </Drawer>
            <Drawer
                variant="persistent"
                anchor="left"
                classes={{ paper: 'confirmDrawerPaper' }}
                open={drawerExtended}
            >
                <Grid
                    container
                    justifyContent="space-between"
                    className="FilterButtonGroup"
                >
                    <Grid
                        item
                        xs={4}
                        className="FilterButtonAlignCenter"
                    >
                        <Button
                            variant="contained"
                            color="secondary"
                            size="medium"
                            className="DrawerFooterActionButton"
                            onClick={() => { setDrawerExtended(false); }}
                        >
                            {tShiftlines('close')}
                        </Button>
                    </Grid>
                    <Grid
                        item
                        xs={4}
                        className="FilterButtonAlignCenter"
                    >
                        <Button
                            variant="contained"
                            color="secondary"
                            size="medium"
                            className="DrawerFooterActionButton"
                            onClick={() => { handleReset(); }}
                        >
                            {tShiftlines('reset')}
                        </Button>
                    </Grid>
                    <Grid
                        item
                        xs={4}
                        className="FilterButtonAlignCenter"
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            size="medium"
                            className="DrawerFooterActionButton"
                            onClick={() => { handleConfirm(); }}
                        >
                            {tShiftlines('confirm')}
                        </Button>
                    </Grid>
                </Grid>
            </Drawer>
            {
                showAutoOrderButton && (
                    <Button
                        className="table-numbering-modal-button"
                        onClick={() => setShowAutoOrderModal(true)}
                        variant="contained"
                        color="primary"
                    >
                        {tShiftlines('addCurrentOrder')}
                    </Button>
                )
            }
            <Button
                className="table-filter-button"
                variant="outlined"
                onClick={() => { setDrawerExtended(true); }}
            >
                <FontAwesomeIcon
                    icon={faFilter}
                />
                {tShiftlines('filtersButton')}
                {' '}
                (
                {nbAppliedFilters}
                )
            </Button>
            <div className="filtered-selection-count">
                {`${employeeBidSelection.filteredItems.length} 
                ${tShiftlines('shiftlinesMatch', { multiple: employeeBidSelection.filteredItems.length > 1 ? 's' : '' })} 
                ${employeeBidSelection.items.length}`}
            </div>
            {
                !isSelectedButtonHidden && (
                    <div className="selected-switch-container">
                        <Switch
                            color="primary"
                            onChange={() =>
                                handleTabSelected(isShowAll
                                    ? ShiftlineFilterSelectionTabs.Selected
                                    : ShiftlineFilterSelectionTabs.Unselected)}
                        />
                        <div className="selected-switch-label">{tShiftlines('showSelected')}</div>
                    </div>
                )
            }
            {
                weeklyHours && (
                    <TotalHoursDisplay
                        defaultValue={weeklyHoursByDefault}
                        onChange={(checked) => updateShowWeeklyHours(checked, dispatch)}
                    />
                )
            }
        </div>
    );
}

export default ShiftlineFilterSelectionBar;
